// src/components/ui/VipSocialsModal.jsx

import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { Checkbox } from './Checkbox';

// Form Options
const vipEventTypeOptions = [
    { value: 'luxury-product-launch', label: 'Luxury Product Launch' },
    { value: 'influencer-media-event', label: 'Influencer / Media Event' },
    { value: 'charity-gala', label: 'Charity Gala / Fundraiser' },
    { value: 'exclusive-vip-dinner', label: 'Exclusive VIP Dinner' },
    { value: 'art-fashion-show', label: 'Art / Fashion Show' },
    { value: 'brand-activation', label: 'Luxury Brand Activation' },
];

const eventObjectives = [
    { id: 'brand-prestige', label: 'Build Brand Prestige' },
    { id: 'media-buzz', label: 'Generate Media & Social Buzz' },
    { id: 'high-value-networking', label: 'High-Value Networking' },
    { id: 'client-appreciation', label: 'Client / Stakeholder Appreciation' },
    { id: 'fundraising', label: 'Fundraising' },
];

const VipSocialsModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        phone: '',
        email: '',
        eventType: '',
        objectives: [],
        targetAudience: '',
        eventDate: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleCheckboxToggle = (objectiveId) => {
        setFormData(prev => ({
            ...prev,
            objectives: prev.objectives.includes(objectiveId)
                ? prev.objectives.filter(id => id !== objectiveId)
                : [...prev.objectives, objectiveId]
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'A contact name is required';
        if (!formData.company.trim()) newErrors.company = 'Company/Brand name is required';
        if (!formData.phone.trim()) newErrors.phone = 'A phone number is required';
        if (!formData.eventType) newErrors.eventType = 'Please select an event type';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);

        const whatsappNumber = "+254727937010";
        const eventTypeLabel = vipEventTypeOptions.find(opt => opt.value === formData.eventType)?.label;
        const selectedObjectives = formData.objectives.map(objId => eventObjectives.find(o => o.id === objId)?.label).join(', ');

        let message = `*💎 VIP & Lifestyle Event Briefing Request 💎*\n\n`;
        message += `*Contact:* ${formData.name}\n`;
        message += `*Company/Brand:* ${formData.company}\n`;
        message += `*Phone:* ${formData.phone}\n`;
        if(formData.email) message += `*Email:* ${formData.email}\n\n`;
        message += `*Event Brief:*\n`;
        message += `  - *Event Type:* ${eventTypeLabel}\n`;
        if(formData.eventDate) message += `  - *Proposed Date:* ${formData.eventDate}\n`;
        if(formData.targetAudience) message += `  - *Target Audience:* ${formData.targetAudience}\n`;
        if(selectedObjectives) message += `\n*Primary Objectives:*\n${selectedObjectives}\n`;
        if (formData.message) message += `\n*Initial Concept/Message:*\n${formData.message}`;

        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="font-serif font-bold text-2xl text-gray-900">Confidential Briefing</h2>
                            <p className="font-sans text-gray-500 mt-1">Provide preliminary details for your elite event.</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} className="-mt-2 -mr-2">
                            <Icon name="X" size={24} />
                        </Button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Contact Name" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={errors.name} />
                        <Input label="Company / Brand" required value={formData.company} onChange={(e) => handleInputChange('company', e.target.value)} error={errors.company} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Phone Number" type="tel" required value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={errors.phone} />
                        <Input label="Email Address (Optional)" type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} />
                    </div>
                    <div>
                        <Select label="Type of Event" required options={vipEventTypeOptions} value={formData.eventType} onChange={(value) => handleInputChange('eventType', value)} error={errors.eventType} />
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-3 block">Primary Objectives</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {eventObjectives.map((obj) => (
                                <Checkbox key={obj.id} label={obj.label} checked={formData.objectives.includes(obj.id)} onChange={() => handleCheckboxToggle(obj.id)} />
                            ))}
                        </div>
                    </div>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Target Audience (Optional)" type="text" value={formData.targetAudience} onChange={(e) => handleInputChange('targetAudience', e.target.value)} placeholder="e.g., HNWIs, Media, Influencers" />
                        <Input label="Proposed Date (Optional)" type="date" value={formData.eventDate} onChange={(e) => handleInputChange('eventDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-2 block">Event Concept or Message (Optional)</label>
                        <textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} placeholder="Share any initial ideas or key information..." rows={3} className="w-full p-3 border border-gray-300 rounded-lg font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                        <Button type="submit" variant="primary" loading={isSubmitting} fullWidth size="lg">
                            {isSubmitting ? 'Sending...' : 'Submit Confidential Brief'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default VipSocialsModal;